// src/components/Users/Forms/Register.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { register, googleAuth, reset, resendVerification } from '../../../redux/slices/Users/authSlice';
import { Helmet } from 'react-helmet-async';

// Must match the rule in the API's registerUserCtrl.
const isStrongPassword = (pw) => pw.length >= 8 && pw.length <= 72 && /[A-Za-z]/.test(pw) && /\d/.test(pw);

const Register = () => {
  const { t, i18n } = useTranslation('common');
  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const { fullname, email, password, confirmPassword } = formData;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isLoading, isError, message, pendingVerificationEmail } = useSelector((state) => state.auth);
  const [passwordMatchError, setPasswordMatchError] = useState('');
  const [resent, setResent] = useState(false);
  const lang = i18n.language?.startsWith('en') ? 'en' : 'es';

  useEffect(() => {
    if (user) {
      navigate('/profile');
    }
    dispatch(reset());
  }, [user, navigate, dispatch]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setPasswordMatchError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isStrongPassword(password)) {
      setPasswordMatchError(t('register.passwordWeak'));
      return;
    }
    if (password !== confirmPassword) {
      setPasswordMatchError(t('register.passwordMismatch'));
      return;
    }
    dispatch(register({ fullname, email, password, confirmPassword, lang }));
  };

  const handleResend = async () => {
    try {
      await resendVerification({ email: pendingVerificationEmail, lang });
    } catch {
      // Rate limited or offline - the message below stays generic either way.
    }
    setResent(true);
  };

  const handleGoogleSuccess = (credentialResponse) => {
    dispatch(googleAuth(credentialResponse.credential));
  };

  const handleGoogleError = () => {
    // El propio botón de Google ya muestra su error; solo evitamos que
    // truene si el usuario cierra el popup o falla la carga del script.
    console.error('Google sign-up failed');
  };

  // Error message to display (either server error or password mismatch)
  const errorMessage = isError ? message : passwordMatchError;

  if (pendingVerificationEmail) {
    return (
      <>
        <Helmet>
          <title>{t('meta.register.title')}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-teal-100 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-6 bg-white p-8 rounded-2xl shadow-xl text-center">
            <div className="text-5xl" aria-hidden="true">📧</div>
            <h2 className="text-3xl font-extrabold text-gray-900">{t('register.checkEmailTitle')}</h2>
            <p className="text-gray-700">{t('register.checkEmailText', { email: pendingVerificationEmail })}</p>
            <p className="text-sm text-gray-500">{t('register.checkEmailSpam')}</p>
            {resent ? (
              <p className="text-sm text-green-700">{t('register.resent')}</p>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                className="text-sm font-medium text-red-600 hover:text-red-500 underline"
              >
                {t('register.resendButton')}
              </button>
            )}
            <div>
              <Link
                to="/login"
                className="inline-block w-full py-2 px-4 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                {t('register.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('meta.register.title')}</title>
        <meta name="description" content={t('meta.register.description')} />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-teal-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl">
          <div>
            {/* Dog image - 15% larger (h-32 w-32 instead of h-28 w-28) */}
            <div className="flex justify-center mb-4">
              <img
                src="https://res.cloudinary.com/dc6xthiol/image/upload/v1783624009/20250920_175720_knw2md.jpg"
                alt={t('register.imageAlt')}
                className="h-32 w-32 rounded-full object-cover shadow-lg"
              />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {t('register.title')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('register.subtitle')}
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {errorMessage && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative">
                {errorMessage}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="fullname" className="block text-sm font-medium text-gray-700">
                  {t('register.fullNameLabel')}
                </label>
                <input
                  id="fullname"
                  name="fullname"
                  type="text"
                  autoComplete="name"
                  required
                  value={fullname}
                  onChange={handleChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder={t('register.fullNamePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('register.emailLabel')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={handleChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder={t('register.emailPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('register.passwordLabel')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={handleChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder={t('register.passwordPlaceholder')}
                  minLength={8}
                  maxLength={72}
                  aria-describedby="password-rules"
                />
                <p id="password-rules" className="mt-1 text-xs text-gray-500">
                  {t('register.passwordRules')}
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  {t('register.confirmPasswordLabel')}
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={handleChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder={t('register.confirmPasswordPlaceholder')}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${
                  isLoading
                    ? 'bg-red-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                }`}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin"></div>
                ) : (
                  t('register.submitButton')
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t('register.dividerText')}</span>
            </div>
          </div>

          {/* Google Sign-up Button */}
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              text="signup_with"
              shape="rectangular"
            />
          </div>

          <div className="text-sm text-center">
            <span className="text-gray-600">{t('register.alreadyHaveAccount')} </span>
            <Link to="/login" className="font-medium text-red-600 hover:text-red-500">
              {t('register.signInLink')}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;